'use client';

import { CellContext } from '@tanstack/react-table';
import ToggleHeaderCell from 'src/components/Tables/cells/ToggleHeaderCell';
import { DataTableColumnDef } from 'src/components/primitives/Table/types';
import { RelativeDateDisplay } from 'src/components/Tables/RelativeDateDisplay';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';
import { MarketCell } from 'src/components/PositionsTable/cells';
import { PnlCell } from './cells/PnlCell';
import { ExplorerCell } from './cells/ExplorerCell';
import { PositionHistoryRowData } from './types';

export interface PositionHistoryTableMeta {
	displayDatesAsTimestamp: boolean;
	onShareClick: (row: PositionHistoryRowData) => void;
}

interface ColumnOptions {
	onToggleDateDisplay: () => void;
}

const EMPTY_VALUE = <span className="text-text-tertiary">-</span>;

function getMeta(
	context: CellContext<PositionHistoryRowData, unknown>
): PositionHistoryTableMeta {
	return context.table.options.meta as PositionHistoryTableMeta;
}

export function getPositionHistoryColumns(
	options: ColumnOptions
): DataTableColumnDef<PositionHistoryRowData>[] {
	const { onToggleDateDisplay } = options;

	return [
		{
			id: 'market',
			header: 'Market',
			meta: {
				width: { ratio: 16, minPx: 120 },
				bodyCellClassName: 'first:pl-0',
				headerCellClassName: 'first:xl:pl-md',
			},
			cell: ({ row }) => (
				<MarketCell
					marketIndex={row.original.uiMarket.marketIndex}
					marketSymbol={row.original.marketName}
					direction={row.original.direction}
					hideLeverage
					hidePositionType
					isIsolated={false}
				/>
			),
		},
		{
			id: 'time',
			header: () => (
				<ToggleHeaderCell label="Time" onClick={onToggleDateDisplay} />
			),
			meta: { width: { ratio: 14, minPx: 100 } },
			cell: (context) => {
				const { displayDatesAsTimestamp } = getMeta(context);
				return (
					<RelativeDateDisplay
						date={context.row.original.date}
						displayAsTimestamp={displayDatesAsTimestamp}
					/>
				);
			},
		},
		{
			id: 'pnl',
			header: () => (
				<DefinitionTooltip definitionId="positionHistoryPnl">
					P&L
				</DefinitionTooltip>
			),
			meta: { width: { ratio: 14, minPx: 100 } },
			cell: (context) => {
				const { onShareClick } = getMeta(context);
				return (
					<PnlCell
						row={context.row.original}
						onShareClick={() => onShareClick(context.row.original)}
					/>
				);
			},
		},
		{
			id: 'size',
			header: 'Size',
			meta: { width: { ratio: 14, minPx: 100 } },
			cell: ({ row }) => {
				const { baseAssetAmountFilledIsZero, baseClosedForPnlDisplay } =
					row.original;
				return baseAssetAmountFilledIsZero
					? EMPTY_VALUE
					: baseClosedForPnlDisplay;
			},
		},
		{
			id: 'entryPrice',
			header: 'Entry Price',
			meta: { width: { ratio: 14, minPx: 100 } },
			cell: ({ row }) => row.original.entryPrice.toNotional(true),
		},
		{
			id: 'exitPrice',
			header: 'Avg. Exit Price',
			meta: { width: { ratio: 14, minPx: 100 } },
			cell: ({ row }) => {
				const { baseAssetAmountFilledIsZero, avgExitPrice } = row.original;
				return baseAssetAmountFilledIsZero
					? EMPTY_VALUE
					: avgExitPrice.toNotional(true);
			},
		},
		{
			id: 'pricePercent',
			header: () => (
				<DefinitionTooltip definitionId="positionHistoryPricePercentChange">
					Price %
				</DefinitionTooltip>
			),
			meta: { width: { ratio: 14, minPx: 90 } },
			cell: ({ row }) => {
				const { baseAssetAmountFilledIsZero, pricePercentChangeDisplay } =
					row.original;
				return baseAssetAmountFilledIsZero
					? EMPTY_VALUE
					: pricePercentChangeDisplay;
			},
		},
		{
			id: 'explorer',
			header: '',
			meta: {
				width: { fixedPx: 32 },
				bodyCellClassName: 'p-0 xl:pr-md',
				headerCellClassName: 'xl:pr-md',
			},
			cell: ({ row }) => <ExplorerCell txSig={row.original.txSig} />,
		},
	];
}
